import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useConfirm } from 'material-ui-confirm';

import { noWheelInputProps } from 'util/utils';
import {
  copiedOptionPayload,
  editedGroupPayload,
  groupWithEditedOption,
  linkedOptionPayload,
  newOptionPayload,
  optionLink,
  optionsInOrder,
  optionsWithout,
  relinkedOptions,
} from '../menuEditorPayloads';
import SortableList from './SortableList';
import ColorField from './ColorField';
import OptionDialog from './OptionDialog';

export const groupFormFrom = (group) => ({
  name: (group && group.name) || '',
  required: !!(group && group.required),
  allow_quantity: !!(group && group.allow_quantity),
  force_min: group && group.force_min != null ? String(group.force_min) : '0',
  force_max: group && group.force_max != null ? String(group.force_max) : '0',
  sort: group && group.sort != null ? String(group.sort) : '99',
  hideForWeb: !!(group && group.hideForWeb),
  colorHex: (group && group.colorHex) || null,
});

export const groupFormErrors = (form) => {
  const errors = {};
  if (!form.name.trim()) {
    errors.name = 'Name is required';
  }
  if (Number(form.force_min) < 0) {
    errors.force_min = 'Cannot be negative';
  }
  if (Number(form.force_max) < 0) {
    errors.force_max = 'Cannot be negative';
  }
  if (form.required && (!form.force_min || Number(form.force_min) < 1)) {
    errors.force_min = 'A required group needs at least 1';
  }
  return errors;
};

/** Name, rules and appearance of a choice group, shared by "new group" and "edit group". */
export const GroupSettingsForm = ({ form, onChange, errors, isNew }) => {
  const set = (key) => (event) => onChange({ ...form, [key]: event.target.value });
  const toggle = (key) => (event) => onChange({ ...form, [key]: event.target.checked });
  return (
    <Stack spacing={2.5}>
      <TextField label="Group name" required fullWidth value={form.name} onChange={set('name')} error={!!errors.name && form.name !== ''} helperText={' '} autoFocus={isNew} />
      <RadioGroup
        row
        value={form.required ? 'required' : 'optional'}
        onChange={(event) => {
          const required = event.target.value === 'required';
          // Choosing "Required" needs at least one pick, as in the classic editor.
          onChange({ ...form, required, force_min: required && Number(form.force_min) < 1 ? '1' : form.force_min });
        }}
      >
        <FormControlLabel value="optional" control={<Radio />} label="Optional" />
        <FormControlLabel value="required" control={<Radio />} label="Required" />
      </RadioGroup>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="Minimum picks"
          type="number"
          value={form.force_min}
          onChange={set('force_min')}
          error={!!errors.force_min}
          helperText={errors.force_min || ' '}
          inputProps={{ ...noWheelInputProps, min: 0 }}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Maximum picks"
          type="number"
          value={form.force_max}
          onChange={set('force_max')}
          error={!!errors.force_max}
          helperText={errors.force_max || '0 means no limit'}
          inputProps={{ ...noWheelInputProps, min: 0 }}
          sx={{ flex: 1 }}
        />
        {!isNew && (
          <TextField label="Sort" type="number" value={form.sort} onChange={set('sort')} helperText=" " inputProps={noWheelInputProps} sx={{ width: 100 }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <FormControlLabel control={<Switch checked={form.allow_quantity} onChange={toggle('allow_quantity')} />} label="Allow the same choice more than once" />
        <FormControlLabel control={<Switch checked={form.hideForWeb} onChange={toggle('hideForWeb')} />} label="Hide from web ordering" />
      </Box>
      {!isNew && (
        <ColorField
          label="Accent colour"
          value={form.colorHex}
          onChange={(colorHex) => onChange({ ...form, colorHex })}
          helperText="Saving with a colour also applies it to every choice in the group"
        />
      )}
    </Stack>
  );
};

GroupSettingsForm.propTypes = {
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNew: PropTypes.bool,
};

const GroupEditor = ({ group, menuId, categories, usedBy, currencySymbol, showIds, actions, onDeleted }) => {
  const confirm = useConfirm();
  const [form, setForm] = useState(groupFormFrom(group));
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    setForm(groupFormFrom(group));
    setDialog(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  const errors = groupFormErrors(form);
  const dirty = JSON.stringify(form) !== JSON.stringify(groupFormFrom(group));
  const options = group.options || [];

  const run = async (work) => {
    setSaving(true);
    try {
      return await work();
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = () => run(() => actions.updateGroup(editedGroupPayload(group, form, menuId)));

  const remove = async () => {
    try {
      await confirm({
        title: `Delete “${group.name}”?`,
        description:
          usedBy > 0
            ? `It is removed from the ${usedBy} dish${usedBy === 1 ? '' : 'es'} that use it, and all its choices are deleted.`
            : 'All its choices are deleted too.',
        confirmationText: 'Delete',
        confirmationButtonProps: { color: 'error' },
      });
    } catch (cancelled) {
      return;
    }
    if (await run(() => actions.deleteGroup(group.id))) {
      onDeleted(group.id);
    }
  };

  const fixLinks = async () => {
    try {
      await confirm({
        title: 'Repair linked choices?',
        description: 'Each linked choice is pointed at the dish with the same name. This also relinks choices that are not broken.',
        confirmationText: 'Repair',
      });
    } catch (cancelled) {
      return;
    }
    const { options: next, fixed } = relinkedOptions(options, categories);
    if (fixed === 0) {
      await confirm({ title: 'Nothing to repair', description: 'No linked choices needed a new dish.', hideCancelButton: true }).catch(() => {});
      return;
    }
    await run(() => actions.saveGroupOptions(group, next));
  };

  const deleteOption = async (option) => {
    try {
      await confirm({ title: `Delete “${option.name}”?`, confirmationText: 'Delete', confirmationButtonProps: { color: 'error' } });
    } catch (cancelled) {
      return;
    }
    await run(() => actions.saveGroupOptions(group, optionsWithout(options, option)));
  };

  const saveOption = (optionForm, link) => {
    if (dialog.mode === 'edit') {
      return actions.updateGroup(groupWithEditedOption(group, dialog.option, optionForm, menuId));
    }
    if (dialog.mode === 'link') {
      return actions.createOption(linkedOptionPayload(optionForm, group, link));
    }
    return actions.createOption(newOptionPayload(optionForm, group));
  };

  return (
    <Paper variant="outlined" sx={{ height: '100%', overflowY: 'auto' }} component="section" aria-label={`Choice group ${group.name}`}>
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {group.name || 'No name'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            <Chip size="small" label={group.required ? 'Required' : 'Optional'} color={group.required ? 'primary' : 'default'} variant="outlined" />
            <Chip size="small" label={`Used by ${usedBy} dish${usedBy === 1 ? '' : 'es'}`} sx={{ bgcolor: 'grey.100' }} />
            {group.hideForWeb && <Chip size="small" icon={<VisibilityOffOutlinedIcon />} label="Hidden from web" variant="outlined" />}
            {showIds && <Chip size="small" variant="outlined" label={`ID ${group.id}`} sx={{ fontFamily: 'monospace' }} />}
          </Box>
        </Box>
        <Tooltip title="Copy group">
          <IconButton aria-label="Copy group" onClick={() => run(() => actions.copyGroup(group))} disabled={saving}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Repair linked choices">
          <IconButton aria-label="Repair linked choices" onClick={fixLinks} disabled={saving}>
            <BuildOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete group">
          <IconButton aria-label="Delete group" onClick={remove} disabled={saving}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Choices <Typography component="span" color="text.secondary">({options.length})</Typography>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<LinkIcon />} onClick={() => setDialog({ mode: 'link' })}>
              Add a dish
            </Button>
            <Button size="small" variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setDialog({ mode: 'new' })}>
              Add choice
            </Button>
          </Box>
        </Box>
        {options.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No choices yet.
          </Typography>
        )}
        <Box sx={{ border: options.length ? '1px solid' : 0, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <SortableList
            type={`editor-options-${group.id}`}
            items={options}
            getKey={(option) => option.id || option.name}
            disabled={saving}
            onReorder={(from, to) => run(() => actions.saveGroupOptions(group, optionsInOrder(options, from, to)))}
            renderItem={(option, index, { rowRef, handleRef, isDragging }) => {
              const link = optionLink(option, categories);
              return (
                <Box
                  ref={rowRef}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pr: 1,
                    py: 0.75,
                    borderTop: index === 0 ? 0 : '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    opacity: isDragging ? 0.4 : 1,
                  }}
                >
                  <Box ref={handleRef} sx={{ display: 'flex', pl: 0.5, color: 'text.disabled', cursor: 'grab' }} aria-label={`Drag to reorder ${option.name}`}>
                    <DragIndicatorIcon fontSize="small" />
                  </Box>
                  {option.colorHex && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: option.colorHex, flexShrink: 0 }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                      {option.shortName ? (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}
                          · {option.shortName}
                        </Typography>
                      ) : null}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {link && (
                        <Typography variant="caption" color={link.broken ? 'error' : 'text.secondary'}>
                          {link.label}
                        </Typography>
                      )}
                      {(option.default ?? option.isDefault) && (
                        <Typography variant="caption" color="primary">
                          Selected by default
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {option.hideForWeb && (
                    <Tooltip title="Hidden from web">
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </Tooltip>
                  )}
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}>
                    {currencySymbol}
                    {Number(option.price || 0).toFixed(2)}
                  </Typography>
                  <IconButton size="small" aria-label={`Edit ${option.name}`} onClick={() => setDialog({ mode: 'edit', option })}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label={`Copy ${option.name}`} disabled={saving} onClick={() => run(() => actions.createOption(copiedOptionPayload(option, group, menuId)))}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label={`Delete ${option.name}`} disabled={saving} onClick={() => deleteOption(option)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            }}
          />
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Group settings
        </Typography>
        <GroupSettingsForm form={form} onChange={setForm} errors={errors} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          {dirty && (
            <Button color="inherit" onClick={() => setForm(groupFormFrom(group))}>
              Discard
            </Button>
          )}
          <Button variant="contained" disableElevation onClick={saveSettings} disabled={!dirty || saving || Object.keys(errors).length > 0}>
            Save settings
          </Button>
        </Box>
      </Box>

      <OptionDialog
        open={!!dialog}
        mode={dialog ? dialog.mode : 'new'}
        option={dialog && dialog.option}
        categories={categories}
        currencySymbol={currencySymbol}
        onClose={() => setDialog(null)}
        onSave={saveOption}
      />
    </Paper>
  );
};

GroupEditor.propTypes = {
  group: PropTypes.object.isRequired,
  menuId: PropTypes.string.isRequired,
  categories: PropTypes.array.isRequired,
  usedBy: PropTypes.number.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  showIds: PropTypes.bool,
  actions: PropTypes.object.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

export default GroupEditor;
